import { FC } from 'react'
import { Link } from 'react-router-dom'
import { RetroButton } from '@/components/retro'

export const LicenseScreen: FC = () => {
  return (
    <div className="min-h-screen bg-game-bg p-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-pixel text-xl text-game-accent">LICENSE AGREEMENT</h1>
          <Link to="/">
            <RetroButton variant="default" size="sm">BACK</RetroButton>
          </Link>
        </div>

        {/* Content */}
        <div className="border-2 border-game-border bg-game-panel p-6 space-y-6">
          <p className="font-retro text-sm text-game-muted">
            Last Updated: January 30, 2025
          </p>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">END USER LICENSE AGREEMENT (EULA)</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              This End User License Agreement ("Agreement") is a legal agreement between you ("User") and Trench Quest ("Company," "we," or "us") for the use of the Trench Quest application ("App").
            </p>
            <p className="font-retro text-base text-game-text leading-relaxed">
              By using the App, you agree to be bound by the terms of this Agreement. If you do not agree, do not use the App.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">1. LICENSE GRANT</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              Subject to your compliance with this Agreement, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the App for personal, non-commercial entertainment purposes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">2. RESTRICTIONS</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              You agree NOT to:
            </p>
            <ul className="font-retro text-base text-game-text leading-relaxed list-disc list-inside space-y-1">
              <li>Copy, modify, or distribute the App or its content</li>
              <li>Reverse engineer, decompile, or disassemble the App</li>
              <li>Use the App for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use bots, scripts, or automated tools to interact with the App</li>
              <li>Exploit bugs or glitches for unfair advantage</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Impersonate any person or entity</li>
              <li>Use the App to promote real financial products or services</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">3. INTELLECTUAL PROPERTY</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              The App, including all content, features, graphics, and functionality, is owned by Trench Quest and is protected by copyright, trademark, and other intellectual property laws. The Trench Quest name, logo, and all related names, logos, and slogans are trademarks of Trench Quest.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">4. USER CONTENT</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              Any content you create or submit through the App remains yours. However, by submitting content, you grant us a worldwide, royalty-free license to use, display, and distribute that content in connection with the App.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">5. SIMULATED TRADING DISCLAIMER</h2>
            <div className="p-4 border-2 border-game-gold bg-game-gold/10">
              <p className="font-retro text-base text-game-text leading-relaxed">
                <strong>IMPORTANT:</strong> Trench Quest is a SIMULATION GAME for entertainment purposes only. All trading, prices, and portfolio values within the game are FICTIONAL and do not involve real cryptocurrency transactions. No real money or cryptocurrency is risked, gained, or lost while playing.
              </p>
            </div>
            <p className="font-retro text-base text-game-text leading-relaxed">
              The App is NOT a financial service, investment platform, or trading tool. Nothing in the App constitutes financial advice. Past simulated performance does not indicate future real-world results.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">6. THIRD-PARTY SERVICES</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              The App may integrate with third-party services such as Solana wallets and social media platforms. Your use of these services is subject to their respective terms of service. We are not responsible for third-party services.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">7. DISCLAIMERS</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE APP WILL BE ERROR-FREE OR UNINTERRUPTED.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">8. LIMITATION OF LIABILITY</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE APP. OUR TOTAL LIABILITY SHALL NOT EXCEED $100 USD.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">9. INDEMNIFICATION</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              You agree to indemnify and hold harmless Trench Quest and its affiliates from any claims, damages, or expenses arising from your use of the App or violation of this Agreement.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">10. TERMINATION</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              We may terminate or suspend your access to the App at any time, without notice, for any reason, including violation of this Agreement. Upon termination, your license to use the App ceases immediately.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">11. GOVERNING LAW</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              This Agreement shall be governed by the laws of the State of Delaware, United States, without regard to conflict of law principles.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">12. CHANGES TO THIS AGREEMENT</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              We reserve the right to modify this Agreement at any time. Continued use of the App after changes constitutes acceptance of the modified Agreement.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">13. CONTACT</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              For questions about this Agreement, contact us at:
            </p>
            <p className="font-retro text-base text-game-accent">
              legal@trenchquest.com
            </p>
          </section>

          {/* Footer */}
          <div className="pt-6 border-t border-game-border text-center">
            <p className="font-retro text-sm text-game-muted">
              By using Trench Quest, you acknowledge that you have read, understood, and agree to be bound by this Agreement.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
